import { isUiVisibleTask } from '../../../gantt/addTaskUtils';
import type { LinkSnapshot, TaskSnapshot } from '../../../stores/types';
import { dependencyTypeToDhtmlx, mapPlanningApiTaskRowToSnapshot } from '../../../utils/utils';

export type DashboardDataset = {
  tasks: TaskSnapshot[];
  links: LinkSnapshot[];
};

function taskRowId(row: unknown): string {
  if (row == null || typeof row !== 'object') return '';
  const r = row as Record<string, unknown>;
  return String(r.id ?? r.taskId ?? r.task_id ?? '').trim();
}

function isNormalizedSnapshot(row: unknown): row is TaskSnapshot {
  if (row == null || typeof row !== 'object') return false;
  const r = row as TaskSnapshot;
  return typeof r.id === 'string' && typeof r.projectId === 'string' && r.projectId.length > 0;
}

function toTaskSnapshots(rows: unknown[]): TaskSnapshot[] {
  return rows
    .map((row) => (isNormalizedSnapshot(row) ? row : mapPlanningApiTaskRowToSnapshot(row)))
    .filter((t) => t.id != null && String(t.id).trim() !== '');
}

function dedupeTasksById(tasks: TaskSnapshot[]): TaskSnapshot[] {
  const byId = new Map<string, TaskSnapshot>();
  let duplicateCount = 0;
  for (const task of tasks) {
    const id = String(task.id);
    if (byId.has(id)) {
      duplicateCount += 1;
    }
    byId.set(id, task);
  }
  if (duplicateCount > 0) {
    console.warn(
      `[Dashboard] Removed ${duplicateCount} duplicate task row(s) by id (kept latest occurrence).`,
    );
  }
  return Array.from(byId.values());
}

function toLinkSnapshots(rows: unknown[], validTaskIds: Set<string>): LinkSnapshot[] {
  const byId = new Map<string, LinkSnapshot>();
  let duplicateCount = 0;
  let invalidEndpointCount = 0;

  for (const row of rows) {
    if (row == null || typeof row !== 'object') continue;
    const raw = row as Record<string, unknown>;
    const id = raw.id;
    if (id == null) continue;
    const source = String(raw.source ?? '').trim();
    const target = String(raw.target ?? '').trim();
    if (!source || !target || !validTaskIds.has(source) || !validTaskIds.has(target)) {
      invalidEndpointCount += 1;
      continue;
    }
    const key = String(id);
    const link: LinkSnapshot = {
      id: Number(id),
      source,
      target,
      type: dependencyTypeToDhtmlx(String(raw.type ?? '0')),
      lag: raw.lag != null ? Number(raw.lag) : null,
    };
    if (byId.has(key)) {
      duplicateCount += 1;
    }
    byId.set(key, link);
  }

  if (duplicateCount > 0) {
    console.warn(
      `[Dashboard] Removed ${duplicateCount} duplicate link row(s) by id (kept latest occurrence).`,
    );
  }
  if (invalidEndpointCount > 0) {
    console.warn(
      `[Dashboard] Ignored ${invalidEndpointCount} link(s) with missing or unknown source/target.`,
    );
  }

  return Array.from(byId.values());
}

/** Parent task ids among visible tasks (tasks that have at least one child). */
export function getParentTaskIds(tasks: TaskSnapshot[]): Set<string> {
  const parentIds = new Set<string>();
  for (const t of tasks) {
    const parentId = t.parentTaskId != null ? String(t.parentTaskId).trim() : '';
    if (parentId) {
      parentIds.add(parentId);
    }
  }
  return parentIds;
}

/**
 * Leaf / actionable tasks: no children in the visible set (not only `summary` flag).
 * Avoids double-counting parents and children when the API omits `summary: true`.
 */
export function getLeafTasks(tasks: TaskSnapshot[]): TaskSnapshot[] {
  const parentIds = getParentTaskIds(tasks);
  return tasks.filter((t) => !parentIds.has(String(t.id)));
}

/**
 * Normalize, dedupe, and filter dashboard inputs to match Gantt-visible schedule data.
 */
export function prepareDashboardData(rawTasks: unknown[], rawLinks: unknown[]): DashboardDataset {
  const deduped = dedupeTasksById(toTaskSnapshots(rawTasks));
  const visible = deduped.filter((t) => isUiVisibleTask(t, deduped));
  const visibleIds = new Set(visible.map((t) => String(t.id)));
  const links = toLinkSnapshots(rawLinks, visibleIds);
  return { tasks: visible, links };
}

/** @internal Exported for tests — detect duplicate ids in raw API payloads. */
export function countDuplicateRawTaskIds(rows: unknown[]): number {
  const seen = new Set<string>();
  let dupes = 0;
  for (const row of rows) {
    const id = taskRowId(row);
    if (!id) continue;
    if (seen.has(id)) dupes += 1;
    else seen.add(id);
  }
  return dupes;
}
